import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SIMIMOVIES_MANAGED_ADS_ENABLED } from '../../config/ads';
import { useFetchManagedPlacements } from '../../hooks/useFetchManagedPlacements';
import { trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst } from '../../store/selectors/adSelectors';
import { managedPlacementToAd } from '../../utils/adMapper';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';
import StaticAdBanner from './StaticAdBanner';

const INLINE_REQ = Object.freeze([{ placement: 'inline-banner', limit: 1 }]);

/**
 * Fetches `inline-banner` for `pageKey`, merges with `fallbackAd`, tracks impression/click when managed.
 */
export default function ManagedInlineBanner({ pageKey, fallbackAd, variant = 'inline' }) {
  const dispatch = useDispatch();
  const { adDomain, adLocale, adDevice } = useFetchManagedPlacements(pageKey, INLINE_REQ);
  const managed = useSelector((s) => selectAdPlacementFirst(s, pageKey, 'inline-banner'));
  const ad = useMemo(() => managedPlacementToAd(managed, fallbackAd), [managed, fallbackAd]);

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managed?._id) return;
    const key = makeImpressionKey({
      campaign: managed._id,
      page: pageKey,
      placement: 'inline-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managed._id,
          page: pageKey,
          placement: 'inline-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managed, pageKey, adDevice, adDomain, adLocale]);

  const onLinkClick = useCallback(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managed?._id) return;
    dispatch(
      trackAdClick({
        campaign: managed._id,
        page: pageKey,
        placement: 'inline-banner',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managed, pageKey, adDevice, adDomain, adLocale]);

  return <StaticAdBanner ad={ad} variant={variant} pageKey={pageKey} onLinkClick={onLinkClick} />;
}
